import React, { useEffect, useRef, useState } from 'react';
import { IconButton, Tooltip, Snackbar, Alert } from '@mui/material';
import { keyframes } from '@mui/system';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import useSpeechToText from '../hooks/useSpeechToText';

const pulse = keyframes`
  0% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.15); }
  100% { opacity: 1; transform: scale(1); }
`;

const ERROR_MESSAGES = {
  'not-allowed':
    'Microphone is blocked. Allow microphone access for this site in your browser settings and try again.',
  'service-not-allowed':
    'Microphone is blocked. Allow microphone access for this site in your browser settings and try again.',
  network: 'Speech service unavailable — check your connection and try again.',
  'no-speech': "Didn't hear anything — tap the mic and speak.",
  'audio-capture': 'No microphone found on this device.',
};

const messageForError = (code) => {
  if (!code || code === 'aborted') return null;
  if (ERROR_MESSAGES[code]) return ERROR_MESSAGES[code];
  return `Voice input failed (${code}). Please try again.`;
};

const VoiceInputButton = ({
  onTranscript,
  onInterim,
  disabled = false,
  size = 'small',
  tooltip = 'Dictate',
}) => {
  const { supported, listening, interimTranscript, finalTranscript, start, stop, error } =
    useSpeechToText();
  const wasListeningRef = useRef(false);
  const [alertMessage, setAlertMessage] = useState(null);

  useEffect(() => {
    if (listening && onInterim) {
      const combined = `${finalTranscript} ${interimTranscript}`.trim();
      if (combined) onInterim(combined);
    }
  }, [listening, interimTranscript, finalTranscript, onInterim]);

  useEffect(() => {
    if (wasListeningRef.current && !listening) {
      const text = finalTranscript.trim();
      if (text && onTranscript) onTranscript(text);
    }
    wasListeningRef.current = listening;
  }, [listening, finalTranscript, onTranscript]);

  useEffect(() => {
    const message = messageForError(error);
    if (message) setAlertMessage(message);
  }, [error]);

  if (!supported) return null;

  const handleClick = () => {
    if (listening) {
      stop();
    } else {
      setAlertMessage(null);
      start();
    }
  };

  const handleAlertClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setAlertMessage(null);
  };

  return (
    <>
      <Tooltip title={listening ? 'Stop dictation' : tooltip}>
        <span>
          <IconButton
            aria-label={listening ? 'stop dictation' : 'start dictation'}
            onClick={handleClick}
            disabled={disabled}
            size={size}
            color={listening ? 'error' : 'default'}
            sx={
              listening
                ? { animation: `${pulse} 1.4s ease-in-out infinite` }
                : undefined
            }
          >
            {listening ? <StopIcon fontSize={size} /> : <MicIcon fontSize={size} />}
          </IconButton>
        </span>
      </Tooltip>
      <Snackbar
        open={Boolean(alertMessage)}
        autoHideDuration={6000}
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleAlertClose} severity="warning" sx={{ width: '100%' }}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default VoiceInputButton;
